import React, { useState, useEffect } from "react";
import { Modal, Button, Spin, Table, Alert, Tag } from "antd";
import DateSelector from "./DateSelector";
import api from "../../../../api/axios";
import { format } from "date-fns";

// Shows an employee's user-tracking samples (browser / IP / device + location)
// for a chosen day. Timestamps are rendered in the viewer's local time.
const UserTrackingModal = ({ visible, onClose, employee }) => {
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const getTodayDateString = () => format(new Date(), "yyyy-MM-dd");

  useEffect(() => {
    if (visible && employee) {
      const today = getTodayDateString();
      setSelectedDate(today);
      fetchTracking(today);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, employee]);

  const fetchTracking = async (date) => {
    if (!employee?.id) return;
    setLoading(true);
    try {
      const data = await api.get(`api/tracking/${employee.id}?date=${date}`);
      setRecords(data?.data?.locations || []);
    } catch (error) {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    fetchTracking(date);
  };

  // captured_at is stored as epoch seconds on the server.
  const toLocalTime = (epochSeconds) =>
    epochSeconds ? new Date(epochSeconds * 1000).toLocaleString() : "N/A";

  const columns = [
    {
      title: "Time (Local)",
      dataIndex: "captured_at",
      key: "captured_at",
      render: (value) => toLocalTime(value),
    },
    {
      title: "IP Address",
      dataIndex: "ip_address",
      key: "ip_address",
      render: (text) => text || "N/A",
    },
    {
      title: "Browser",
      key: "browser",
      render: (_, record) =>
        [record.browser_name, record.browser_version]
          .filter(Boolean)
          .join(" ") || "N/A",
    },
    {
      title: "OS",
      dataIndex: "os_name",
      key: "os_name",
      render: (text) => text || "N/A",
    },
    {
      title: "Device",
      dataIndex: "device_type",
      key: "device_type",
      render: (text) => (text ? <Tag>{text}</Tag> : "N/A"),
    },
    {
      title: "Location",
      key: "location",
      render: (_, record) =>
        record.latitude != null && record.longitude != null ? (
          <a
            href={`https://www.google.com/maps?q=${record.latitude},${record.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {Number(record.latitude).toFixed(5)},{" "}
            {Number(record.longitude).toFixed(5)}
          </a>
        ) : (
          "N/A"
        ),
    },
    {
      title: "Accuracy (m)",
      dataIndex: "accuracy",
      key: "accuracy",
      render: (text) => (text != null ? text : "N/A"),
    },
  ];

  const employeeName = employee
    ? `${employee.first_name || ""} ${employee.last_name || ""}`.trim()
    : "";

  return (
    <Modal
      title={`User Tracking Details${employeeName ? ` - ${employeeName}` : ""}`}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1100}
      zIndex={99999999}
    >
      <Button
        onClick={onClose}
        style={{
          position: "absolute",
          top: "9px",
          right: "7px",
          border: "none",
          background: "transparent",
          fontSize: "18px",
          fontWeight: "bold",
          cursor: "pointer",
        }}
      >
        ×
      </Button>
      <DateSelector
        selectedDate={selectedDate}
        onDateChange={handleDateChange}
      />
      {loading ? (
        <Spin
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "40px 0",
          }}
          size="large"
        />
      ) : records.length > 0 ? (
        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />
      ) : (
        <Alert
          message="No tracking data available for the selected date"
          type="warning"
          showIcon
        />
      )}
    </Modal>
  );
};

export default UserTrackingModal;
